import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography } from '@altermaind/web/ui';
import { Tooltip } from '@/_shared/components/Tooltip';
import { Routes } from '@/_shared/constants/routes';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getSurveysCampaignsCall } from '@/surveys';
import { SendSurvey } from '@/surveys/components';

type Props = {
  total: number;
  completed: number;
  invalid: number;
};

export const RecapPeople: FC<Props> = ({ total, completed, invalid }) => {
  const t = useTranslations('people.recapPeople');
  const { genericResponsiveValue } = useResponsiveStyle();
  const navigate = useNavigate();

  const [, setTrigger] = useState(false);

  const { execute: executeGetSurveysCampaigns } = getSurveysCampaignsCall.hook();

  const {
    isOpen: isOpenSendSurvey,
    closeDialog: closeDialogSendSurvey,
    openDialog: openDialogSendSurvey,
  } = useDialog('sendSurvey');

  const box1 = useRef(null);
  const box2 = useRef(null);

  const goToPeopleReview = useCallback(() => {
    navigate(Routes.PeopleReview);
  }, [navigate]);

  useEffect(() => {
    if (box1.current && box2.current) {
      const heightBox1 = (box1.current as HTMLDivElement).offsetHeight;
      const heightBox2 = (box2.current as HTMLDivElement).offsetHeight;
      if (heightBox1 > heightBox2) {
        (box2.current as HTMLDivElement).style.height = `${heightBox1}px`;
      }
      if (heightBox2 > heightBox1) {
        (box1.current as HTMLDivElement).style.height = `${heightBox2}px`;
      }
    }
  }, [box1, box2]);

  useEffect(() => {
    executeGetSurveysCampaigns({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      display='flex'
      flexDirection={genericResponsiveValue({
        breakPoints: { desktop: 'row' },
        defaultValue: 'column',
      })}
      justifyContent='space-between'
      alignItems='center'
      mb='s'
    >
      <Box
        mb={genericResponsiveValue({
          breakPoints: { desktop: 'none' },
          defaultValue: 's',
        })}
        ref={box1}
        flex='1'
        display='flex'
        flexDirection='row'
        height='100%'
        width={genericResponsiveValue({
          breakPoints: { desktop: 'auto' },
          defaultValue: '100%',
        })}
      >
        <Card
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'column' },
            defaultValue: 'column',
          })}
          flex='1'
          height='100%'
          justifyContent='space-between'
          mr={genericResponsiveValue({
            breakPoints: { desktop: 'xs' },
            defaultValue: 'none',
          })}
        >
          <Box display='flex' flexDirection='column'>
            <Box display='flex' alignItems='center'>
              <Typography variant='h2' mt='none'>
                {t('current.title')}
              </Typography>
              <Tooltip
                iconProps={{ size: 'xs', fill: 'grey/light' }}
                containerProps={{ ml: 'xxs' }}
              >
                {t('current.tooltip')}
              </Tooltip>
            </Box>
            <Typography variant='h1' mt='xxxs'>
              {total}
            </Typography>
          </Box>
          {invalid > 0 ? (
            <>
              <Box
                mt='xs'
                display='flex'
                flexDirection={genericResponsiveValue({
                  breakPoints: { desktop: 'row' },
                  defaultValue: 'column',
                })}
                alignItems={genericResponsiveValue({
                  breakPoints: { desktop: 'center' },
                  defaultValue: 'flex-start',
                })}
              >
                <Box
                  mb={genericResponsiveValue({
                    breakPoints: { desktop: 'none' },
                    defaultValue: 'xs',
                  })}
                  flex='1'
                >
                  <Box display='flex' alignItems='center'>
                    <Typography variant='h4' mt='none'>
                      {t('validProfile.title')}
                    </Typography>
                    <Tooltip
                      iconProps={{ size: 'xs', fill: 'grey/light' }}
                      containerProps={{ ml: 'xxs' }}
                    >
                      {t('validProfile.tooltip')}
                    </Tooltip>
                  </Box>
                  <Typography variant='p2/light' mt='xxxs'>
                    {completed}
                  </Typography>
                </Box>
                <Box flex='1'>
                  <Box display='flex' alignItems='center'>
                    <Typography variant='h4' mt='none'>
                      {t('invalidProfile.title')}
                    </Typography>
                    <Tooltip
                      iconProps={{ size: 'xs', fill: 'grey/light' }}
                      containerProps={{ ml: 'xxs' }}
                    >
                      {t('invalidProfile.tooltip')}
                    </Tooltip>
                  </Box>
                  <Typography color='error' variant='p2/light' mt='xxxs'>
                    {invalid}
                  </Typography>
                </Box>
              </Box>
              <Box mt='xs' display='flex' flexDirection='row' alignItems='center'>
                <Box
                  flex='1'
                  display='flex'
                  justifyContent={genericResponsiveValue({
                    breakPoints: { desktop: 'flex-end' },
                    defaultValue: 'flex-start',
                  })}
                  onClick={goToPeopleReview}
                >
                  <Button variant='primary'>{t('cta.fixProfiles')}</Button>
                </Box>
              </Box>
            </>
          ) : (
            <Box>
              <Typography variant='p2/light'>{t('noInvalid.text1')}</Typography>
              <Typography variant='p2/light'>{t('noInvalid.text2')}</Typography>
            </Box>
          )}
        </Card>
      </Box>
      <Box ref={box2} flex='1' display='flex' flexDirection='row' height='100%'>
        <Card
          display='flex'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
          flex='1'
          backgroundColor='primary'
          height='100%'
        >
          <Box mt='xxxxs' mr='xxs'>
            <Svg name='book' size='s' />
          </Box>
          <Box flex='1' display='flex' flexDirection='column' justifyContent='space-between'>
            <Box width='100%'>
              <Typography variant='h4' mt='none'>
                {t('survey.title')}
              </Typography>
              <Typography minHeight='48px' variant='p2/light' mt='xxxs'>
                {t('survey.description')}
              </Typography>
            </Box>
            <Box
              onClick={openDialogSendSurvey}
              display='flex'
              justifyContent={genericResponsiveValue({
                breakPoints: { desktop: 'flex-end' },
                defaultValue: 'flex-start',
              })}
              alignSelf='flex-end'
            >
              <Button mt='s' variant='positive' fontSize='m'>
                {t('survey.buttonLabel')}
              </Button>
            </Box>
          </Box>
        </Card>
      </Box>
      <SendSurvey
        setTrigger={setTrigger}
        preSelection
        isOpen={isOpenSendSurvey}
        closeDialog={closeDialogSendSurvey}
      />
    </Box>
  );
};
